"use client";

import { v4 as uuid } from "uuid";

export interface PromptRecord {
	id: string;
	prompt: string; // 翻译后的提示词
	originalPrompt: string; // 原始英文提示词
	image: string; // base64 string
	createdAt: number;
	tags?: string[];
	modelType: string; // 使用的模型类型
}

class LocalStorageService {
	private readonly DB_NAME = "image-prompt-db";
	private readonly STORE_NAME = "prompts";
	private readonly DB_VERSION = 1;
	private readonly MAX_RECORDS = 20;
	private db: IDBDatabase | null = null;

	constructor() {
		// 只在浏览器环境中初始化
		if (typeof window !== "undefined") {
			this.initDB();
		}
	}

	private async initDB(): Promise<void> {
		// 确保在浏览器环境中
		if (typeof window === "undefined" || !window.indexedDB) {
			return Promise.reject(new Error("IndexedDB is not available"));
		}

		return new Promise((resolve, reject) => {
			const request = window.indexedDB.open(
				this.DB_NAME,
				this.DB_VERSION,
			);

			request.onerror = () => {
				console.error("Error opening database");
				reject(request.error);
			};

			request.onsuccess = () => {
				this.db = request.result;
				resolve();
			};

			request.onupgradeneeded = (event) => {
				const db = (event.target as IDBOpenDBRequest).result;
				if (!db.objectStoreNames.contains(this.STORE_NAME)) {
					const store = db.createObjectStore(this.STORE_NAME, {
						keyPath: "id",
					});
					store.createIndex("createdAt", "createdAt", {
						unique: false,
					});
					store.createIndex("tags", "tags", {
						unique: false,
						multiEntry: true,
					});
					store.createIndex("modelType", "modelType", {
						unique: false,
					});
				}
			};
		});
	}

	private async ensureDB(): Promise<void> {
		if (!this.db && typeof window !== "undefined") {
			await this.initDB();
		}
	}

	private async getOldestRecord(): Promise<PromptRecord | null> {
		await this.ensureDB();
		if (!this.db) return null;

		return new Promise((resolve, reject) => {
			const transaction = this.db!.transaction(
				[this.STORE_NAME],
				"readonly",
			);
			const store = transaction.objectStore(this.STORE_NAME);
			const index = store.index("createdAt");

			const request = index.openCursor();

			request.onsuccess = (event) => {
				const cursor = (event.target as IDBRequest).result;
				if (cursor) {
					resolve(cursor.value);
				} else {
					resolve(null);
				}
			};

			request.onerror = () => reject(request.error);
		});
	}

	private async getRecordCount(): Promise<number> {
		await this.ensureDB();
		if (!this.db) return 0;

		return new Promise((resolve, reject) => {
			const transaction = this.db!.transaction(
				[this.STORE_NAME],
				"readonly",
			);
			const store = transaction.objectStore(this.STORE_NAME);
			const request = store.count();

			request.onsuccess = () => resolve(request.result);
			request.onerror = () => reject(request.error);
		});
	}

	async savePrompt(
		prompt: string,
		originalPrompt: string,
		image: string,
		modelType: string,
		tags?: string[],
	): Promise<string> {
		await this.ensureDB();
		if (!this.db) throw new Error("Database not initialized");

		const count = await this.getRecordCount();

		// 如果达到最大记录数，删除最早的记录
		if (count >= this.MAX_RECORDS) {
			const oldestRecord = await this.getOldestRecord();
			if (oldestRecord) {
				await this.deletePrompt(oldestRecord.id);
			}
		}

		return new Promise((resolve, reject) => {
			const transaction = this.db!.transaction(
				[this.STORE_NAME],
				"readwrite",
			);
			const store = transaction.objectStore(this.STORE_NAME);

			const record: PromptRecord = {
				id: uuid(),
				prompt,
				originalPrompt,
				image,
				createdAt: Date.now(),
				tags,
				modelType,
			};

			const request = store.add(record);

			request.onsuccess = () => resolve(record.id);
			request.onerror = () => reject(request.error);
		});
	}

	async getPrompt(id: string): Promise<PromptRecord | null> {
		await this.ensureDB();
		if (!this.db) return null;

		return new Promise((resolve, reject) => {
			const transaction = this.db!.transaction(
				[this.STORE_NAME],
				"readonly",
			);
			const store = transaction.objectStore(this.STORE_NAME);
			const request = store.get(id);

			request.onsuccess = () => resolve(request.result || null);
			request.onerror = () => reject(request.error);
		});
	}

	async getAllPrompts(): Promise<PromptRecord[]> {
		await this.ensureDB();
		if (!this.db) return [];

		return new Promise((resolve, reject) => {
			const transaction = this.db!.transaction(
				[this.STORE_NAME],
				"readonly",
			);
			const store = transaction.objectStore(this.STORE_NAME);
			const index = store.index("createdAt");

			const request = index.openCursor(null, "prev");
			const results: PromptRecord[] = [];

			request.onsuccess = (event) => {
				const cursor = (event.target as IDBRequest).result;
				if (cursor) {
					results.push(cursor.value);
					cursor.continue();
				} else {
					resolve(results);
				}
			};

			request.onerror = () => reject(request.error);
		});
	}

	async deletePrompt(id: string): Promise<void> {
		await this.ensureDB();
		if (!this.db) return;

		return new Promise((resolve, reject) => {
			const transaction = this.db!.transaction(
				[this.STORE_NAME],
				"readwrite",
			);
			const store = transaction.objectStore(this.STORE_NAME);
			const request = store.delete(id);

			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	async searchByTags(tags: string[]): Promise<PromptRecord[]> {
		await this.ensureDB();
		if (!this.db) return [];

		return new Promise((resolve, reject) => {
			const transaction = this.db!.transaction(
				[this.STORE_NAME],
				"readonly",
			);
			const store = transaction.objectStore(this.STORE_NAME);
			const index = store.index("tags");
			const results: PromptRecord[] = [];

			const request = index.openCursor();

			request.onsuccess = (event) => {
				const cursor = (event.target as IDBRequest).result;
				if (cursor) {
					const record = cursor.value as PromptRecord;
					if (tags.every((tag) => record.tags?.includes(tag))) {
						results.push(record);
					}
					cursor.continue();
				} else {
					resolve(results);
				}
			};

			request.onerror = () => reject(request.error);
		});
	}

	async clearAll(): Promise<void> {
		await this.ensureDB();
		if (!this.db) return;

		return new Promise((resolve, reject) => {
			const transaction = this.db!.transaction(
				[this.STORE_NAME],
				"readwrite",
			);
			const store = transaction.objectStore(this.STORE_NAME);
			const request = store.clear();

			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}
}

// 创建单例实例
export const localStorageService = new LocalStorageService();
